import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ResponsiveContainer,
  LineChart, Line,
  BarChart, Bar,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { Syringe, UserCog, Wrench } from "lucide-react";

import { ChartCard, Empty, RankingCard, Heatmap } from "./widgets";
import { COLORS, brl } from "./utils";

/**
 * The 6 inline tabs of /analytics. State (activeTab) and data come from the
 * parent so the same data can be reused by the presentation overlay.
 */
export default function AnalyticsTabs({
  activeTab, setActiveTab, data, totals,
  monthSeries, pieShift, pieSusp, weekdaySeries,
  topSurgeons, topProcs, topAnesth, topHospitals,
}) {
  const t = totals;
  return (
    <Tabs value={activeTab} onValueChange={setActiveTab}>
      <TabsList className="bg-white border border-slate-200 overflow-x-auto flex-wrap">
        <TabsTrigger value="geral" data-testid="tab-geral">Visão geral</TabsTrigger>
        <TabsTrigger value="pessoas" data-testid="tab-pessoas">Por pessoa</TabsTrigger>
        <TabsTrigger value="procedimentos" data-testid="tab-procs">Procedimentos</TabsTrigger>
        <TabsTrigger value="financeiro" data-testid="tab-fin">Financeiro</TabsTrigger>
        <TabsTrigger value="heatmap" data-testid="tab-heatmap">Heatmap</TabsTrigger>
        <TabsTrigger value="suspensoes" data-testid="tab-susp">Suspensões</TabsTrigger>
      </TabsList>

      <TabsContent value="geral" className="space-y-5 pt-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <ChartCard title="Tendência mensal — casos" className="lg:col-span-2">
            {monthSeries.length === 0 ? <Empty /> : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={monthSeries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="total" stroke="#1d4ed8" strokeWidth={2} name="Casos" dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          <ChartCard title="Por turno">
            {pieShift.length === 0 ? <Empty /> : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={pieShift} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => `${e.name}: ${e.value}`}>
                    {pieShift.map((entry, i) => <Cell key={`shift-${entry.name}`} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <ChartCard title="Por dia da semana">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={weekdaySeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="weekday" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="total" fill="#1d4ed8" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Casos por status">
            {(data?.by_status || []).length === 0 ? <Empty /> : (
              <div className="flex flex-wrap gap-2 pt-2">
                {(data?.by_status || []).map((s) => (
                  <span key={s.status} className="px-2.5 py-1 rounded-full border border-slate-200 bg-slate-50 text-xs text-slate-700">
                    {s.status.replace(/_/g, " ")} · <b>{s.total}</b>
                  </span>
                ))}
              </div>
            )}
          </ChartCard>
        </div>
      </TabsContent>

      <TabsContent value="pessoas" className="space-y-5 pt-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <RankingCard icon={UserCog} title="Top cirurgiões e auxiliares" rows={topSurgeons} subtitle="Soma cirurgião principal + auxiliar" />
          <RankingCard icon={Syringe} title="Top anestesistas" rows={topAnesth} />
          <RankingCard icon={Wrench} title="Top instrumentadores" rows={(data?.by_technician || []).slice(0, 10)} />
        </div>
      </TabsContent>

      <TabsContent value="procedimentos" className="space-y-5 pt-4">
        <ChartCard title="Top procedimentos (horizontal)">
          {topProcs.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={Math.max(260, topProcs.length * 32)}>
              <BarChart data={topProcs} layout="vertical" margin={{ left: 120 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={150} />
                <Tooltip />
                <Bar dataKey="total" fill="#059669" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Top hospitais">
          {topHospitals.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={Math.max(220, topHospitals.length * 32)}>
              <BarChart data={topHospitals} layout="vertical" margin={{ left: 120 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={150} />
                <Tooltip />
                <Bar dataKey="total" fill="#0891b2" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </TabsContent>

      <TabsContent value="financeiro" className="space-y-5 pt-4">
        <ChartCard title="Evolução financeira mensal (Aprovado × Faturado × Recebido)">
          {monthSeries.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={monthSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                <Tooltip formatter={(v) => brl(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="approved" stroke="#7c3aed" strokeWidth={2} name="Aprovado" />
                <Line type="monotone" dataKey="billed" stroke="#1d4ed8" strokeWidth={2} name="Faturado" />
                <Line type="monotone" dataKey="received" stroke="#059669" strokeWidth={2} name="Recebido" />
                <Line type="monotone" dataKey="adjustments" stroke="#dc2626" strokeWidth={1.5} strokeDasharray="4 2" name="Glosa" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Faturamento por cirurgião (top 10)">
          {topSurgeons.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={Math.max(260, topSurgeons.length * 34)}>
              <BarChart data={topSurgeons} layout="vertical" margin={{ left: 120 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={150} />
                <Tooltip formatter={(v) => brl(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="approved" fill="#7c3aed" name="Aprovado" />
                <Bar dataKey="billed" fill="#1d4ed8" name="Faturado" />
                <Bar dataKey="received" fill="#059669" name="Recebido" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </TabsContent>

      <TabsContent value="heatmap" className="pt-4">
        <ChartCard title="Cirurgião × Procedimento (top 10 × top 10)">
          <Heatmap hm={data?.heatmap} />
        </ChartCard>
      </TabsContent>

      <TabsContent value="suspensoes" className="space-y-5 pt-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <ChartCard title="Suspensões por motivo">
            {pieSusp.length === 0 ? <Empty text="Sem suspensões no período." /> : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={pieSusp} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => `${e.name}: ${e.value}`}>
                    {pieSusp.map((entry, i) => <Cell key={`susp-${entry.name}`} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
          <ChartCard title="Resumo">
            <div className="space-y-2 pt-2">
              <div className="text-sm text-slate-600">
                <strong className="text-red-600">{t.cases_suspended ?? 0}</strong> suspensões no período filtrado.
              </div>
              <ul className="space-y-1.5">
                {(data?.suspensions_by_reason || []).map((s) => (
                  <li key={s.reason} className="flex items-center justify-between text-sm border-b border-slate-100 pb-1.5">
                    <span className="text-slate-700">{s.label}</span>
                    <span className="font-semibold text-slate-900">{s.total}</span>
                  </li>
                ))}
              </ul>
            </div>
          </ChartCard>
        </div>
      </TabsContent>
    </Tabs>
  );
}
